import type { CSSProperties, FocusEventHandler, MouseEventHandler, ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { PORTED, PORTED_PREFIXES } from '~/ported-routes'
import { opening } from '~/sheet/seed'
import type { SeedRow } from '~/sheet/seed'

/**
 * A link that knows whether its destination has been ported yet.
 *
 * During the migration most of the site is still served by Python. A react-router `<Link>`
 * to one of those addresses is intercepted by the router, matches no route, and renders an
 * empty page — so the reader clicks a gig and gets a blank screen rather than the gig.
 *
 * Six of the eight links in the first ported components had this bug, and the reason is
 * worth recording: `Masthead` — written as the reference for the others to copy — used
 * `NavLink to="/calendar"` while `/calendar` was still Python's. Every agent that copied the
 * reference copied the defect. One porter noticed and wrote a plain anchor with a comment
 * explaining why; the rest followed the pattern they were shown.
 *
 * So the decision is not a convention to remember at each call site. It is this list, and
 * a route joins it in the same commit that makes it real.
 */
function isPorted(to: string): boolean {
  if ((PORTED as readonly string[]).includes(to)) return true
  // `/e/:id` and friends carry a parameter, so membership is a prefix. Without this every
  // gig link on every card would leave the app for a page Python no longer needs to render.
  return (PORTED_PREFIXES as readonly string[]).some(prefix => to.startsWith(prefix))
}

export interface AppLinkProps {
  to: string
  children: ReactNode
  className?: string
  title?: string
  tabIndex?: number
  'aria-hidden'?: boolean | 'true' | 'false'
  'aria-label'?: string
  'aria-current'?: 'page' | undefined
  /** For a link that also has something behind it — the masthead name and its menu. */
  'aria-expanded'?: boolean
  'aria-haspopup'?: 'menu'
  /** For a link that is an item in a menu. The router is not told; ARIA is. */
  role?: 'menuitem'
  /**
   * For a link whose stylesheet reads a number off it — the faces stack sets its `--size`
   * here, so one rule draws every size the surfaces ask for. Custom properties are not in
   * `CSSProperties`, so a caller casts; the escape hatch is this prop, not a wrapper span,
   * because the test for a surface reads the size off the stack's own root.
   */
  style?: CSSProperties
  'data-size'?: number
  'data-past'?: 'true'
  onFocus?: FocusEventHandler<HTMLAnchorElement>
  onBlur?: FocusEventHandler<HTMLAnchorElement>
  /**
   * For a link inside something else clickable — a venue name on a list row, where the row
   * opens the gig. `stopPropagation` belongs to the caller because only the caller knows
   * what is underneath.
   */
  onClick?: MouseEventHandler<HTMLAnchorElement>
  /**
   * The row this link was drawn from, for a link to a gig's sheet. Carried in the
   * navigation so the sheet can draw the row's title, picture and night before `/event`
   * answers — see `~/sheet/seed`. Ignored for any other address.
   */
  seed?: SeedRow
}

/** The addresses that open over a page rather than replacing it. */
// `/a/` is not here: an act is a page you arrive at, not something you glance at over
// what you were reading. A gig still opens as a sheet, which is what this is for.
const OVER = ['/e/']

export function AppLink({ to, children, seed, ...rest }: AppLinkProps) {
  const here = useLocation()

  // A full navigation, deliberately: Python answers this address and the SPA must get out
  // of the way rather than swallow the click.
  if (!isPorted(to)) {
    return (
      <a href={to} {...rest}>
        {children}
      </a>
    )
  }
  // A link to a sheet carries the page it was pressed on, so the sheet opens over it rather
  // than replacing it. Nothing else needs to know: `App` reads it, and a link opened cold —
  // pasted, bookmarked, sent — simply has none. A link drawn from a row carries that row's
  // seed beside it, and a cold link has none of that either.
  const state = OVER.some(prefix => to.startsWith(prefix)) ? opening(here, seed) : undefined

  return (
    <NavLink to={to} end={to === '/'} state={state} {...rest}>
      {children}
    </NavLink>
  )
}
